import type { VisibleCardViewV1 } from "@fate-kingdom/engine";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useMemo, useRef } from "react";
import { PerspectiveCamera, type Group } from "three";

import type { AlphaCardDragState } from "../alpha/drag/alpha-drag-types";
import { createPhysicalCardFaceSpec } from "./CardFaceTextureCache";
import {
  getPhysicalHandCardTransform,
  physicalHandCameraDistance,
  screenPointToCameraPlane,
} from "./hand-world-layout";
import {
  projectCardScreenRect,
  roundedScreenRect,
  screenRectsDiffer,
  type HandScreenLayout,
} from "./hand-screen-layout";
import { PhysicalCard3D } from "./PhysicalCard3D";

export interface PlayerHand3DProps {
  readonly cards: readonly VisibleCardViewV1[];
  readonly draggedCard: VisibleCardViewV1 | null;
  readonly selectedCardId?: string | undefined;
  readonly legalCardIds: ReadonlySet<string>;
  readonly dragState: AlphaCardDragState;
  readonly collapsed: boolean;
  readonly isHumanTurn: boolean;
  readonly interactionBlocked: boolean;
  readonly reducedMotion: boolean;
  readonly focusedCardId?: string | undefined;
  readonly onScreenLayoutChange: (layout: HandScreenLayout) => void;
}

export function PlayerHand3D({
  cards,
  draggedCard,
  selectedCardId,
  legalCardIds,
  dragState,
  collapsed,
  isHumanTurn,
  interactionBlocked,
  reducedMotion,
  focusedCardId,
  onScreenLayoutChange,
}: PlayerHand3DProps) {
  const groupRef = useRef<Group>(null);
  const cardGroupsRef = useRef(new Map<string, Group>());
  const lastScreenLayoutRef = useRef<HandScreenLayout>({});
  const lastProjectionAtRef = useRef(0);
  const viewport = useThree((state) => state.size);
  const camera = useThree((state) => state.camera);
  const visibleCards = useMemo(() => {
    if (
      draggedCard === null ||
      cards.some((card) => card.cardInstanceId === draggedCard.cardInstanceId)
    ) {
      return cards;
    }
    return [...cards, draggedCard];
  }, [cards, draggedCard]);
  const activeDragCardId =
    dragState.status === "pressed" ||
    dragState.status === "dragging" ||
    dragState.status === "dropping"
      ? (dragState.cardInstanceId ?? undefined)
      : undefined;
  const activeCardId = activeDragCardId ?? selectedCardId;
  const selectedIndex =
    activeCardId === undefined
      ? null
      : visibleCards.findIndex((card) => card.cardInstanceId === activeCardId);

  const setCardGroup = useCallback((cardInstanceId: string, group: Group | null) => {
    if (group === null) cardGroupsRef.current.delete(cardInstanceId);
    else cardGroupsRef.current.set(cardInstanceId, group);
  }, []);

  useFrame(({ clock }) => {
    const group = groupRef.current;
    if (group === null) return;
    group.position.copy(camera.position);
    group.quaternion.copy(camera.quaternion);
    group.updateMatrixWorld(true);
    if (clock.elapsedTime - lastProjectionAtRef.current < 1 / 30) return;
    lastProjectionAtRef.current = clock.elapsedTime;
    const nextLayout: Record<string, ReturnType<typeof projectCardScreenRect>> = {};
    for (const card of visibleCards) {
      const cardGroup = cardGroupsRef.current.get(card.cardInstanceId);
      if (cardGroup === undefined) continue;
      nextLayout[card.cardInstanceId] = roundedScreenRect(
        projectCardScreenRect(cardGroup, camera, viewport.width, viewport.height),
      );
    }
    if (!screenRectsDiffer(lastScreenLayoutRef.current, nextLayout)) return;
    lastScreenLayoutRef.current = nextLayout;
    onScreenLayoutChange(nextLayout);
  });

  return (
    <group ref={groupRef} name="physical-player-hand">
      {visibleCards.map((card, index) => {
        const isActive = card.cardInstanceId === activeCardId;
        const travelling =
          isActive && (dragState.status === "dragging" || dragState.status === "dropping");
        const layout = getPhysicalHandCardTransform({
          index,
          count: visibleCards.length,
          selectedIndex: selectedIndex === null || selectedIndex < 0 ? null : selectedIndex,
          viewportWidth: viewport.width,
          viewportHeight: viewport.height,
          collapsed,
        });
        const dragDistance = Math.max(
          4.75,
          physicalHandCameraDistance(viewport.width, viewport.height) - 0.65,
        );
        const dragPosition = travelling
          ? screenPointToCameraPlane({
              clientX: dragState.currentX,
              clientY: dragState.currentY - (dragState.pointerType === "touch" ? 58 : 22),
              viewportWidth: viewport.width,
              viewportHeight: viewport.height,
              distance: dragDistance,
              fovDegrees: camera instanceof PerspectiveCamera ? camera.fov : 40,
            })
          : layout.position;
        const velocityTilt = reducedMotion
          ? 0
          : Math.max(-0.065, Math.min(0.065, dragState.velocityX * -0.035));
        const playable = legalCardIds.has(card.cardInstanceId);
        return (
          <PhysicalCard3D
            face={createPhysicalCardFaceSpec(card, {
              subtitle: playable ? "Ready to play" : isHumanTurn ? "Waiting" : "Opponent turn",
            })}
            position={dragPosition}
            rotation={
              travelling
                ? [
                    Math.max(-0.075, Math.min(0.075, dragState.velocityY * 0.03)),
                    velocityTilt,
                    velocityTilt,
                  ]
                : layout.rotation
            }
            scale={travelling ? layout.scale * 1.04 : layout.scale}
            selected={isActive}
            focused={card.cardInstanceId === focusedCardId}
            legal={playable && !interactionBlocked}
            dimmed={!isHumanTurn || interactionBlocked || !playable}
            reducedMotion={reducedMotion}
            renderOrder={travelling ? 1_100 : layout.renderOrder}
            selectionLift={0}
            selectionScale={1}
            selectionTilt={0}
            sceneMetadata={{
              objectKind: "physical-card",
              cardInstanceId: card.cardInstanceId,
              cardDefinitionId: card.cardDefinitionId,
              cardType: card.type,
              ownerSide: "own",
              zone: travelling ? "hand-transfer" : "hand",
              visibleState: "face-up",
            }}
            onGroupRef={(group) => setCardGroup(card.cardInstanceId, group)}
            key={card.cardInstanceId}
          />
        );
      })}
    </group>
  );
}
